import { useQuery } from '@tanstack/react-query';
import { useParams, useRouteLoaderData } from 'react-router';

import { SideBarPaddingWrapper, SideBarSectionContainer } from 'DesignSystem/features/sidebar';
import SidebarDetailContent from 'DesignSystem/features/sidebar/SidebarDetailContent';
import { useIsDataFieldNotApplicable } from 'hooks/bootstrap/not-applicable-data-fields';
import { prefCardsBootstrapQuery, usePrefCardCases } from 'pages/preference-cards/api';
import { selectAssigneeOptions } from 'pages/preference-cards/api/selectors';
import ClariumStatusPill from 'pages/preference-cards/components/shared/badges/clarium-status';
import {
  CaseType,
  LocationDetails,
  PhysicianDetails,
  ServiceLineDetails,
} from 'pages/preference-cards/components/shared/sidebar/details-info';
import { ProcedureCodesList } from 'pages/preference-cards/components/shared/sidebar/procedure-codes-list';
import type { PrefCardsDetailsResponse } from 'pages/preference-cards/types';
import { SelectOption } from 'types';
import { NOT_APPLICABLE_DATA_FIELD_KEYS } from 'utils/constants/notApplicableDataFields';

import { PrefCardAssigneeSelect } from '../../../shared/assignee-select/preference-cards';
import { CardCasesDialog } from '../../cases-dialog/card-cases-dialog';

const NO_OPTIONS: SelectOption[] = [];

export default function CardSideBarDetails() {
  const { details } = useRouteLoaderData('co-card') as { details: PrefCardsDetailsResponse };
  const { cardId } = useParams<'cardId'>();
  const hideApprovedOrLocation = useIsDataFieldNotApplicable(NOT_APPLICABLE_DATA_FIELD_KEYS.APPROVED_OR_LOCATION);
  const { isSuccess, data: assigneeOptions } = useQuery(prefCardsBootstrapQuery(selectAssigneeOptions));
  const { data: caseCount } = usePrefCardCases(
    cardId!,
    { page_size: '0' },
    { select: (data) => data.result_set_size, enabled: Boolean(cardId) }
  );
  const currentAssignee = details.assignee_id
    ? ({ name: details.assignee_name, value: details.assignee_id } as SelectOption)
    : null;

  return (
    <SideBarPaddingWrapper classNames="border-b">
      <SideBarSectionContainer>
        <SidebarDetailContent classNames="py-0" label="Assignee">
          {isSuccess ? (
            <PrefCardAssigneeSelect
              cardId={cardId!}
              options={assigneeOptions || NO_OPTIONS}
              currentAssignee={currentAssignee}
              variant="ghost"
            />
          ) : null}
        </SidebarDetailContent>
        {details && details.clarium_status_display_name && (
          <SidebarDetailContent label="Status" classNames="pr-1">
            <ClariumStatusPill status={details.clarium_status_display_name} />
          </SidebarDetailContent>
        )}
      </SideBarSectionContainer>

      <SideBarSectionContainer>
        <SidebarDetailContent label="Performing Physician" classNames="pr-1" detailClassNames="max-w-[50%]">
          <PhysicianDetails
            physicians={details.utilizing_physicians}
            primaryPhysician={details.primary_physician_display_name}
          />
        </SidebarDetailContent>
        {!hideApprovedOrLocation ? (
          <SidebarDetailContent label="Approved OR Location" classNames="pr-1" detailClassNames="max-w-[50%]">
            <LocationDetails locations={details.locations} />
          </SidebarDetailContent>
        ) : null}
        <SidebarDetailContent label="Service Lines" classNames="pr-1" detailClassNames="max-w-[50%]">
          <ServiceLineDetails serviceLines={details.service_lines} />
        </SidebarDetailContent>
        <SidebarDetailContent label="Cases in Last 12 Months" classNames="pr-1">
          {typeof caseCount === 'number' ? caseCount > 0 ? <CardCasesDialog caseCount={caseCount} /> : 0 : '–'}
        </SidebarDetailContent>
        <SidebarDetailContent label="Utilizing Case Type" classNames="pr-1">
          <CaseType sharedCaseFrequency={details.metric_shared_case_frequency} />
        </SidebarDetailContent>
      </SideBarSectionContainer>

      <div className="pt-3">
        <ProcedureCodesList mode="read" procedureCodes={details.clinical_procedures} />
      </div>
    </SideBarPaddingWrapper>
  );
}
